import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Select from "react-select";
import { saveAs } from "file-saver";
import { managePayslipActionRequest } from "../../redux/action/managePayslipsAction";
import { postApiCall } from "../../service/PostApiCall";
import {
  DELETE_PAYSLIPS,
  PAY_HELPER,
  PAYSLIP_EXPORT,
} from "../../service/apiUrl";
import DataTable from "../../components/table/DataTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import LoadingButton from "../../components/LoadingButton";
import ActionTooltip from "../../components/ActionTooltip";
import Utils from "../../utility/utility";
import { exportPeriod, payslipCsv } from "./payslipExport";

// payslips store the month as "1".."12" and the year as "2026"
const monthOptions = moment
  .monthsShort()
  .map((label, i) => ({ value: String(i + 1), label }));
const currentYear = moment().year();
const yearOptions = Array.from({ length: 5 }, (_, i) => {
  const year = String(currentYear - i);
  return { value: year, label: year };
});

const rupees = (value) => (
  <span className="text-nowrap">
    <i className="fa fa-rupee mr-1" aria-hidden="true"></i>
    {value}
  </span>
);

const ManagePayslip = (props) => {
  const dispatch = useDispatch();
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;
  const managePayslipList = useSelector((state) => state.managePayslipsReducer);
  const payslipList = managePayslipList?.data?.payslips;

  const [nameSearch, setNameSearch] = useState("");
  const [visibilitySearch, setVisibilitySearch] = useState(null);
  const [monthSearch, setMonthSearch] = useState(null);
  const [yearSearch, setYearSearch] = useState(null);
  const [helperData, setHelperData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // bumped after a delete to refetch the same page
  const [refresh, setRefresh] = useState(0);
  /* What the confirm dialog is about to delete: one row's trash icon or the
     bulk action. `done` clears the table's selection once it is gone. */
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    if (!access_token) return;
    postApiCall([], access_token, PAY_HELPER)
      .then((result) => {
        setHelperData(result ?? {});
      })
      .catch((error) => {
        console.error("PAYSLIP_HELPER", error);
      });
  }, [access_token]);

  useEffect(() => {
    if (!access_token) return;
    dispatch(
      managePayslipActionRequest(
        {
          access_token: access_token,
          search: nameSearch,
          visibility: visibilitySearch?.value ?? "",
          month: monthSearch?.value ?? "",
          year: yearSearch?.value ?? "",
          pageSize: pageSize,
          page: currentPage,
        },
        access_token
      )
    );
  }, [
    access_token,
    dispatch,
    nameSearch,
    visibilitySearch,
    monthSearch,
    yearSearch,
    pageSize,
    currentPage,
    refresh,
  ]);

  // a new filter re-slices the result, so the page it was paired with is meaningless
  const changeFilter = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  /* The rows on screen can be older than the table (another admin, a reseed),
     so a failure or an already-gone payslip both end in a fresh list rather
     than a bare "Try again" over rows that no longer exist. */
  const deletePayslips = async () => {
    const ids = pendingDelete?.ids ?? [];
    if (!access_token || !ids.length) return false;
    let result;
    try {
      result = await postApiCall({ ids }, access_token, DELETE_PAYSLIPS);
    } catch (error) {
      console.error("PAYSLIP_DELETE", error);
      Utils.toast(
        "error",
        error?.data?.message || "The payslips could not be deleted."
      );
      setRefresh((n) => n + 1);
      throw error;
    }
    const missing = Number(result?.missing) || 0;
    if (missing > 0) {
      Utils.toast(
        "warn",
        missing === ids.length
          ? `${missing === 1 ? "This payslip was" : "These payslips were"} already deleted. The list is refreshed.`
          : `${missing} of ${ids.length} payslips were already deleted. The list is refreshed.`
      );
    }
    pendingDelete.done?.();
    // emptying the last page would leave the table on a page that no longer exists
    if (ids.length >= (payslipList?.length ?? 0) && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else {
      setRefresh((n) => n + 1);
    }
    setPendingDelete(null);
  };

  /* Every payslip matching the filters, not just the page on screen. */
  const exportCsv = async () => {
    const result = await postApiCall(
      {
        search: nameSearch,
        visibility: visibilitySearch?.value ?? "",
        month: monthSearch?.value ?? "",
        year: yearSearch?.value ?? "",
      },
      access_token,
      PAYSLIP_EXPORT
    );
    const rows = result?.payslips ?? [];
    if (!rows.length) {
      Utils.toast("warn", "No payslips match these filters.");
      return false;
    }
    // the BOM makes Excel read the file as UTF-8
    const blob = new Blob(["﻿" + payslipCsv(rows)], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(
      blob,
      `payslips-${exportPeriod(monthSearch?.value, yearSearch?.value)}.csv`
    );
  };

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "#",
        enableHiding: false,
        meta: { width: "56px" },
        cell: ({ row }) => (currentPage - 1) * pageSize + row.index + 1,
      },
      {
        id: "name",
        header: "Name",
        enableHiding: false,
        cell: ({ row }) => (
          <span className="text-nowrap fw-medium">
            {row.original?.uid?.name}
          </span>
        ),
      },
      {
        id: "period",
        header: "Month Year",
        cell: ({ row }) => (
          <span className="text-nowrap">
            {monthOptions[Number(row.original?.month) - 1]?.label ??
              row.original?.month}{" "}
            {row.original?.year}
          </span>
        ),
      },
      {
        id: "total_leaves",
        header: "Total Leaves",
        cell: ({ row }) => row.original?.total_leaves,
      },
      {
        id: "actual_salary",
        header: "Actual Salary",
        cell: ({ row }) => rupees(row.original?.actual_salary),
      },
      {
        id: "deduction_percentage",
        header: "Deduction (%)",
        cell: ({ row }) => row.original?.deduction_percentage,
      },
      {
        id: "tax",
        header: "Tax (%)",
        cell: ({ row }) => row.original?.tax_percentage,
      },
      {
        id: "credited_salary",
        header: "Salary Credit",
        cell: ({ row }) => rupees(row.original?.credited_salary),
      },
      {
        id: "created_by",
        header: "Last Update By",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.created_by?.name}</span>
        ),
      },
      {
        id: "visibility",
        header: "Visibility",
        cell: ({ row }) => row.original?.visibility?.label,
      },
      {
        id: "actions",
        header: "",
        enableHiding: false,
        meta: { width: "128px", className: "dt-sticky-end" },
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionTooltip label="View">
              <Link
                to={`/view-payslip/${row.original.id}`}
                aria-label="View payslip"
              >
                <i className="fa fa-eye site-color" aria-hidden="true"></i>
              </Link>
            </ActionTooltip>
            <ActionTooltip label="Edit">
              <Link
                to={`/edit-payslip/${row.original.id}`}
                aria-label="Edit payslip"
              >
                <i className="fa fa-pencil site-color" aria-hidden="true"></i>
              </Link>
            </ActionTooltip>
            <ActionTooltip label="Duplicate">
              <Link
                to={`/duplicate-payslip/${row.original.id}`}
                aria-label="Duplicate payslip"
              >
                <i className="fa fa-clone site-color" aria-hidden="true"></i>
              </Link>
            </ActionTooltip>
            <ActionTooltip label="Delete">
              <button
                type="button"
                className="button-popup"
                aria-label="Delete payslip"
                onClick={() =>
                  setPendingDelete({
                    ids: [row.original.id],
                    done: () => row.toggleSelected(false),
                  })
                }
              >
                <i className="site-color fa fa-trash" aria-hidden="true"></i>
              </button>
            </ActionTooltip>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  const deleteCount = pendingDelete?.ids?.length ?? 0;

  return (
    <>
      <div className={props?.data?.transtion == true ? "contents" : "content"}>
        <div className="content-top-spacer"></div>
        <div className="color-body">
          <div className="page-head page-head-row">
            <div>
              <div className="page-eyebrow">People</div>
              <h1 className="page-title">Manage Payslips</h1>
            </div>
            <div className="page-head-actions">
              <LoadingButton
                className="dt-btn"
                successLabel="Exported"
                onAction={exportCsv}
              >
                <i className="fa fa-download" aria-hidden="true"></i>
                Export CSV
              </LoadingButton>
            </div>
          </div>
          <div className="col-md-12">
            <div className="card-input mt-3">
              <div className="col-md-12 mb-3">
                <div className="mt-4">
                  <DataTable
                    columns={columns}
                    data={payslipList || []}
                    isLoading={managePayslipList?.isLoading === true}
                    getRowId={(row, index) => String(row?.id ?? index)}
                    sorting={false}
                    filters={
                      <div className="listing-filters">
                        <div className="filter-cell">
                          <label className="filter-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search name"
                            value={nameSearch}
                            onChange={(event) =>
                              changeFilter(setNameSearch)(event.target.value)
                            }
                          />
                        </div>
                        <div className="filter-cell">
                          <label className="filter-label">Visibility</label>
                          <Select
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="All"
                            isSearchable={false}
                            isClearable={true}
                            value={visibilitySearch}
                            options={helperData?.visibility ?? []}
                            onChange={changeFilter(setVisibilitySearch)}
                          />
                        </div>
                        <div className="filter-cell">
                          <label className="filter-label">Month</label>
                          <Select
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="All"
                            isSearchable={false}
                            isClearable={true}
                            value={monthSearch}
                            options={monthOptions}
                            onChange={changeFilter(setMonthSearch)}
                          />
                        </div>
                        <div className="filter-cell">
                          <label className="filter-label">Year</label>
                          <Select
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="All"
                            isSearchable={false}
                            isClearable={true}
                            value={yearSearch}
                            options={yearOptions}
                            onChange={changeFilter(setYearSearch)}
                          />
                        </div>
                      </div>
                    }
                    bulkActions={(selectedRows, table) => (
                      <button
                        type="button"
                        className="dt-btn dt-btn-danger"
                        onClick={() =>
                          setPendingDelete({
                            ids: selectedRows.map((row) => row.original.id),
                            done: () => table.resetRowSelection(),
                          })
                        }
                      >
                        <i className="fa fa-trash" aria-hidden="true"></i>
                        Delete selected
                      </button>
                    )}
                    pagination={{
                      manual: true,
                      pageIndex: currentPage - 1,
                      pageSize: pageSize,
                      pageCount: managePayslipList?.data?.total_pages ?? -1,
                      pageSizeOptions: [10, 25, 50, 100],
                      onChange: (next) => {
                        const resized = next.pageSize !== pageSize;
                        setCurrentPage(resized ? 1 : next.pageIndex + 1);
                        setPageSize(next.pageSize);
                      },
                    }}
                    emptyText="No record found"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isShow={deleteCount > 0}
        onHide={() => setPendingDelete(null)}
        title={
          deleteCount > 1
            ? `Delete ${deleteCount} payslips?`
            : "Delete this payslip?"
        }
        description={
          deleteCount > 1
            ? "The selected payslips are removed. This cannot be undone."
            : "The payslip is removed for that month. This cannot be undone."
        }
        confirmLabel="Delete"
        successLabel="Deleted"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={deletePayslips}
      />
    </>
  );
};

export default ManagePayslip;
